/**
 * Enhances the Rebuy Bundle Builder on the Build Your Bundle page:
 * - replaces each card's size dropdown with one listing sizes smallest to largest that drives Rebuy's hidden dropdown, so Rebuy still owns the selection, box and discounts;
 * - adds a line in the box saying how far the next discount tier is.
 * Rebuy renders asynchronously and re-renders often, so the work runs on DOM mutations, batched to
 * one pass per short timer tick, and every step is idempotent.
 */
class EroBundleBuilder extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Record<string, { meals: number, name: string }>} */
  #variants = {};

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      this.#variants = {};
    }

    const root = document.getElementById('MainContent') || document.body;
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(root, { childList: true, subtree: true, characterData: true });
    root.addEventListener('change', this.#onChange);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    (document.getElementById('MainContent') || document.body).removeEventListener('change', this.#onChange);
  }

  #schedule = () => {
    if (this.#timer) return;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#enhance();
    }, 30);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('.rebuy-bundle-builder .rebuy-select')) {
      this.#syncPicker(event.target);
    }
  };

  #enhance() {
    for (const widget of document.querySelectorAll('.rebuy-bundle-builder')) {
      for (const select of widget.querySelectorAll('.rebuy-product-block .rebuy-select')) {
        if (select instanceof HTMLSelectElement) this.#addPicker(select);
      }
      this.#updateNudge(widget);
    }
  }

  /** @param {string} template @param {Record<string, string>} values */
  #fill(template, values) {
    return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template);
  }

  /** @param {HTMLSelectElement} select */
  #addPicker(select) {
    const container = select.parentElement;
    if (!container || select.options.length < 2) return;

    const signature = [...select.options].map((option) => option.value).join(',');
    let picker = container.querySelector('.ero-bb-size');
    if (picker instanceof HTMLElement && picker.dataset.options === signature) {
      this.#syncPicker(select);
      return;
    }
    picker?.remove();

    picker = document.createElement('div');
    picker.className = 'ero-bb-size';
    picker.dataset.options = signature;

    const ours = document.createElement('select');
    ours.className = 'ero-bb-size__select';

    // Smallest size first, largest last.
    const sizes = [...select.options]
      .map((option) => {
        const info = this.#variants[option.value];
        return { option, info, meals: info?.meals || Number(option.text.match(/\d+/)?.[0]) || 0 };
      })
      .sort((a, b) => a.meals - b.meals);

    for (const { option, info, meals } of sizes) {
      const mealsTemplate = (meals === 1 ? this.dataset.mealsOne : this.dataset.mealsOther) || '__COUNT__';
      const mealsText = this.#fill(mealsTemplate, { COUNT: String(meals) });
      const gramsPerMeal = Number(this.dataset.gramsPerMeal) || 0;
      let label = meals ? mealsText : option.text.trim();
      if (meals && gramsPerMeal > 0) {
        const weight = this.#fill(this.dataset.weight || '__GRAMS__g', { GRAMS: String(meals * gramsPerMeal) });
        label = this.#fill(this.dataset.sizeOption || '__WEIGHT__ (__MEALS__)', { WEIGHT: weight, MEALS: mealsText });
      }
      ours.append(new Option(label, option.value));
    }

    ours.addEventListener('change', () => {
      if (select.value !== ours.value) {
        select.value = ours.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.#syncPicker(select);
    });

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'ero-bb-size__chevron');
    chevron.setAttribute('viewBox', '0 0 12 12');
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = '<path d="M2 4.5 6 8l4-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>';

    picker.append(ours, chevron);
    container.append(picker);
    this.#syncPicker(select);
  }

  /** @param {HTMLSelectElement} select */
  #syncPicker(select) {
    const ours = select.parentElement?.querySelector('.ero-bb-size__select');
    if (!(ours instanceof HTMLSelectElement)) return;
    if (ours.value !== select.value) ours.value = select.value;

    // Rebuy can swap in the translated title after the first render.
    const title = select.closest('.rebuy-product-block')?.querySelector('.rebuy-product-title')?.textContent?.trim() || '';
    const label = `${this.dataset.sizesLabel || ''} ${title}`.trim();
    if (ours.getAttribute('aria-label') !== label) ours.setAttribute('aria-label', label);
  }

  /** @param {string | null | undefined} text */
  #parseMoney(text) {
    if (!text) return NaN;
    let value = text.replace(/[^\d.,]/g, '');
    // "1 234,56" / "134,00" style: comma is the decimal separator.
    if (/,\d{2}$/.test(value)) value = value.replace(/\./g, '').replace(',', '.');
    else value = value.replace(/,/g, '');
    return Number.parseFloat(value);
  }

  /**
   * Formats like the shop's money format ("$12.04") so the line matches Rebuy's prices on both languages.
   * @param {number} amount
   */
  #formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  /** @param {Element} widget */
  #updateNudge(widget) {
    const progress = widget.querySelector('.rebuy-bundle-builder__group-container-progress-bar');
    if (!progress?.parentElement) return;

    const tiers = [...progress.querySelectorAll('.rebuy-bundle-builder__progress-step')]
      .map((step) => ({
        threshold: this.#parseMoney(step.querySelector('.rebuy-bundle-builder__progress-step-label')?.textContent),
        percent: step.querySelector('.rebuy-bundle-builder__progress-step-value')?.textContent?.trim() || '',
      }))
      .filter((tier) => Number.isFinite(tier.threshold) && tier.percent)
      .sort((a, b) => a.threshold - b.threshold);
    if (!tiers.length) return;

    // Tiers are measured on the original (undiscounted) subtotal, which is the CTA's compare-at price.
    const cta = widget.querySelector('.rebuy-bundle-builder__cta-container');
    const original =
      this.#parseMoney(cta?.querySelector('.rebuy-money.compare-at')?.textContent) ||
      this.#parseMoney(cta?.querySelector('.rebuy-money.sale')?.textContent) ||
      0;

    const next = tiers.find((tier) => original < tier.threshold);
    let html;
    if (!next) {
      html = this.#fill(this.dataset.unlocked || '', { PERCENT: `<strong>${tiers.at(-1)?.percent}</strong>` });
    } else {
      const template = original > 0 ? this.dataset.nudge : this.dataset.start;
      html = this.#fill(template || '', {
        AMOUNT: `<strong>${this.#formatMoney(next.threshold - original)}</strong>`,
        PERCENT: `<strong>${next.percent}</strong>`,
      });
    }

    let nudge = progress.parentElement.querySelector(':scope > .ero-bb-nudge');
    if (!nudge) {
      nudge = document.createElement('p');
      nudge.className = 'ero-bb-nudge';
      nudge.setAttribute('aria-live', 'polite');
      progress.before(nudge);
    }
    if (nudge.innerHTML !== html) nudge.innerHTML = html;
  }
}

if (!customElements.get('ero-bundle-builder')) customElements.define('ero-bundle-builder', EroBundleBuilder);
